import { decomposePlaceValue, type PlaceValueDigits } from './CrystalNumberBuilderConfig.js';
import type { NumberCoreChallengeConfig } from './NumberCoreChallengeConfig.js';

export type NumberCorePlace = keyof PlaceValueDigits;

export class NumberCoreChallengeState {
  readonly required: PlaceValueDigits;
  readonly counts: PlaceValueDigits;

  constructor(readonly config: NumberCoreChallengeConfig) {
    this.required = decomposePlaceValue(config.target);
    this.validateCounts(config.initial);
    this.counts = { ...config.initial };
  }

  adjust(place: NumberCorePlace, delta: -1 | 1) {
    const next = this.counts[place] + delta;
    if (next < 0 || next > 9) return false;
    this.counts[place] = next;
    return true;
  }

  reset() {
    this.counts.hundreds = this.config.initial.hundreds;
    this.counts.tens = this.config.initial.tens;
    this.counts.ones = this.config.initial.ones;
  }

  get value() {
    return this.counts.hundreds * 100 + this.counts.tens * 10 + this.counts.ones;
  }

  get isCorrect() {
    return this.counts.hundreds === this.required.hundreds
      && this.counts.tens === this.required.tens
      && this.counts.ones === this.required.ones;
  }

  get incorrectPlaces(): NumberCorePlace[] {
    return (['hundreds', 'tens', 'ones'] as const)
      .filter(place => this.counts[place] !== this.required[place]);
  }

  get decomposition() {
    return `${this.counts.hundreds} hundreds + ${this.counts.tens} tens + ${this.counts.ones} ones = ${this.value}`;
  }

  private validateCounts(counts: PlaceValueDigits) {
    (Object.values(counts) as number[]).forEach(count => {
      if (!Number.isInteger(count) || count < 0 || count > 9) {
        throw new RangeError(`Number Core counts must be single digits from 0 to 9; received ${count}.`);
      }
    });
  }
}
